import { createContext, useContext, useState, ReactNode, useEffect } from "react";

// Available currencies with their symbols and codes
export interface Currency {
  code: string;
  symbol: string;
  name: string;
}

export const AVAILABLE_CURRENCIES: Currency[] = [
  { code: 'ILS', symbol: '₪', name: 'Israeli Shekel' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
];

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  formatAmount: (amount: number) => string;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

const CURRENCY_STORAGE_KEY = 'flow-track-currency';

export function CurrencyProvider({ children }: { children: ReactNode }) {
  // Default to ILS (Israeli Shekel)
  const [currency, setCurrencyState] = useState<Currency>(
    AVAILABLE_CURRENCIES.find(c => c.code === 'ILS') || AVAILABLE_CURRENCIES[0]
  );

  // Load currency from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(CURRENCY_STORAGE_KEY);
    if (saved) {
      try {
        const savedCurrency = JSON.parse(saved);
        const foundCurrency = AVAILABLE_CURRENCIES.find(c => c.code === savedCurrency.code);
        if (foundCurrency) {
          setCurrencyState(foundCurrency);
        }
      } catch (error) {
        console.error('Failed to load saved currency:', error);
      }
    }
  }, []);

  const setCurrency = (newCurrency: Currency) => {
    setCurrencyState(newCurrency);
    localStorage.setItem(CURRENCY_STORAGE_KEY, JSON.stringify(newCurrency));
  };

  const formatAmount = (amount: number): string => {
    // Format number with thousand separators and 2 decimal places
    const formattedNumber = Math.abs(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    // Format based on currency code
    if (currency.code === 'ILS') {
      // For ILS, put symbol after the amount: "1,234.56 ₪"
      return `${formattedNumber} ${currency.symbol}`;
    } else {
      // For other currencies, put symbol before: "$1,234.56"
      return `${currency.symbol}${formattedNumber}`;
    }
  };

  const value: CurrencyContextType = {
    currency,
    setCurrency,
    formatAmount,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}; 